import {
  ProductProvider,
  ProductTitle,
  SelectedVariantPrice,
} from "@shopify/hydrogen";
import type { Product } from "@shopify/hydrogen/dist/esnext/graphql/types/types";
import { useInView } from "react-intersection-observer";

import { useCartUI } from "../../hooks/use-cart-ui";
import { useItemDetails } from "../../hooks/use-item-details";
import type { ItemWithCheckoutProduct } from "../../hooks/use-items-with-checkout";
import { useUTMLink } from "../../hooks/use-utm";
import { ActionBar } from "../ActionBar";
import { AspectRatio } from "../AspectRatio";
import { Media } from "../Media";
import { ItemContent } from "../item/ItemContent";
import { ItemDetails } from "../item/ItemDetails";
import { OpenAddToBagModalButton } from "../product/OpenAddToBagModalButton";

export interface FeedItemProps {
  item: ItemWithCheckoutProduct;
  aspectRatio?: number;
}

export function FeedItem({ item, aspectRatio = 1 }: FeedItemProps) {
  const rootElementProps = {
    "data-type": "item",
    "data-id": item.id,
    className: "lookbook--feed--item",
    itemScope: true,
    itemType: "http://schema.org/Product",
  };

  const { ref, inView } = useInView({
    rootMargin: "500px",
  });

  const link = useUTMLink(item.slug);

  const { openItemModal } = useCartUI();

  const initialVariant = item.checkoutProduct?.variants.edges[0].node;

  const itemDetails = useItemDetails(item);

  const { checkoutProduct } = item;

  const contents = (
    <>
      {item.media ? (
        <AspectRatio ratio={aspectRatio}>
          <div className="lookbook--feed--item--image" data-visible={inView}>
            <Media
              visible={inView}
              prefetch={inView}
              prefer="contain"
              size={null}
              media={item.media}
              sizes={[
                ["(min-width: 1024px)", "320px"],
                ["(min-width: 768px)", "235px"],
                ["(min-width: 640px)", "320px"],
                ["100vw"],
              ]}
            />
          </div>
        </AspectRatio>
      ) : null}
      <div className="lookbook--feed--item--content">
        {checkoutProduct ? (
          <ProductProvider
            product={checkoutProduct as Product}
            initialVariantId={initialVariant?.id}>
            <ItemContent>
              <ItemDetails
                {...itemDetails}
                clamped={false}
                price={<SelectedVariantPrice />}
                title={<ProductTitle />}
              />
              <ActionBar
                primaryActions={[
                  {
                    id: "shopify-add-to-cart",
                    content: (
                      <OpenAddToBagModalButton
                        onClick={() => {
                          openItemModal({
                            product: checkoutProduct,
                            isBuyingNow: false,
                          });
                        }}
                      />
                    ),
                  },
                ]}
                secondaryActions={
                  link
                    ? [{ id: "learn-more", href: link, content: "Learn More" }]
                    : undefined
                }
              />
            </ItemContent>
          </ProductProvider>
        ) : (
          <ItemContent>
            <ItemDetails {...itemDetails} clamped={false} />
            <ActionBar
              primaryActions={
                link
                  ? [{ id: "learn-more", href: link, content: "Learn More" }]
                  : item.download_url
                    ? [
                        {
                          id: "download",
                          href: item.download_url,
                          content: "Download",
                        },
                      ]
                    : undefined
              }
            />
          </ItemContent>
        )}
      </div>
    </>
  );

  return (
    <>
      {link && !checkoutProduct ? (
        <a
          {...rootElementProps}
          ref={ref}
          tabIndex={0}
          href={link}
          target="_blank"
          rel="noopener noreferrer">
          {contents}
        </a>
      ) : (
        <span {...rootElementProps} ref={ref}>
          {contents}
        </span>
      )}
    </>
  );
}
